import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AdminLabel, LabelProduct, ProductService } from '../../../_services/product.service';
import { AlertService } from '../../../_services/alert.service';
import { UserService } from '../../../_services/users.service';
import { ConfirmDialogComponent } from '../../../../Shared/_components/confirm-dialog/confirm-dialog.component';
import { canDeleteDeliveryTerms } from '../../delivery-terms/delivery-terms.component';
import { LABEL_STYLE_NAMES, labelOrder, labelStyle } from '../label-style';
import { RecordNavComponent, confirmLeave } from '../../../../Shared/_components/record-nav/record-nav.component';
import { LabelPreviewComponent } from '../label-preview.component';

// Create (/labels/create) or edit (/labels/edit/:id) one label: its
// back-office name and the text shown on the product image, with a live
// preview in the storefront's style. Editing also lists the products it's on.
@Component({
    selector: 'app-label-edit',
    imports: [CommonModule, FormsModule, RouterLink, MainLayoutComponent, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, LabelPreviewComponent, RecordNavComponent],
    templateUrl: './label-edit.component.html'
})
export class LabelEditComponent implements OnInit {
  private productService = inject(ProductService);
  private alertService = inject(AlertService);
  private userService = inject(UserService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  readonly canDelete = canDeleteDeliveryTerms(this.userService.isLoggedIn());
  readonly styleNames = LABEL_STYLE_NAMES;

  // Quick picks for the text on the image.
  readonly suggestions = ['NEW ARRIVAL', 'BESTSELLER', 'POPULAR CHOICE', 'CLEARANCE', 'ALMOST GONE!', 'BACK IN STOCK SOON', 'COMING SOON!', 'FREE SEALER INCLUDED'];

  loading = true;
  notFound = false;
  labels: AdminLabel[] = [];
  editing: AdminLabel | null = null;
  products: LabelProduct[] = [];
  productsLoading = false;

  title = '';
  text = '';

  // Colours: the built-in style for the label, or ones picked here.
  colourMode: 'BUILT_IN' | 'CUSTOM' = 'BUILT_IN';
  bg = '#fdd835';
  textMode: 'AUTO' | 'CUSTOM' = 'AUTO';
  fg = '#111111';
  readonly palette = [
    { name: 'Yellow', hex: '#fdd835' }, { name: 'Amber', hex: '#f59e0b' }, { name: 'Orange', hex: '#f97316' },
    { name: 'Red', hex: '#dc2626' }, { name: 'Rose', hex: '#e11d48' }, { name: 'Pink', hex: '#db2777' },
    { name: 'Purple', hex: '#7c3aed' }, { name: 'Blue', hex: '#2563eb' }, { name: 'Sky', hex: '#0284c7' },
    { name: 'Teal', hex: '#0d9488' }, { name: 'Green', hex: '#3c8d40' }, { name: 'Emerald', hex: '#059669' },
    { name: 'Black', hex: '#111111' }, { name: 'Slate', hex: '#334155' }, { name: 'Stone', hex: '#a8a29e' }, { name: 'White', hex: '#ffffff' },
  ];
  // The text follows the name until it's typed over.
  textTouched = false;
  saving = false;
  private original = '';

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id')) || null;
    this.productService.getAdminLabels().subscribe({
      next: (rows) => {
        this.labels = rows;
        this.loading = false;
        if (id) {
          const l = rows.find((r) => r.id === id);
          if (!l) {
            this.notFound = true;
            return;
          }
          this.editing = l;
          this.title = l.title;
          this.text = l.label;
          this.textTouched = true;
          if (l.bgColor) {
            this.colourMode = 'CUSTOM';
            this.bg = l.bgColor;
            this.fg = l.textColor || this.contrastOf(l.bgColor);
            this.textMode = this.fg === this.contrastOf(l.bgColor) ? 'AUTO' : 'CUSTOM';
          }
          if (l.products) this.loadProducts(l.id);
        }
        this.original = this.snapshot;
      },
      error: () => {
        this.loading = false;
        this.alertService.alert('Could not load labels', 'error');
      },
    });
  }

  private loadProducts(id: number): void {
    this.productsLoading = true;
    this.productService.getLabelProducts(id).subscribe({
      next: (rows) => {
        this.products = rows;
        this.productsLoading = false;
      },
      error: () => (this.productsLoading = false),
    });
  }

  private get snapshot(): string {
    return `${this.title.trim()}|${this.text.trim()}|${this.savedBg}|${this.savedFg}`;
  }

  // ---- Colours ----
  // Black or white, whichever reads better on the background.
  contrastOf(hex: string): string {
    const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
    if (!m) return '#111111';
    const n = parseInt(m[1], 16);
    const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
      const v = c / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return lum > 0.4 ? '#111111' : '#ffffff';
  }

  get effectiveFg(): string {
    return this.textMode === 'AUTO' ? this.contrastOf(this.bg) : this.fg;
  }

  // What gets stored (null = built-in style).
  get savedBg(): string | null {
    return this.colourMode === 'CUSTOM' ? this.bg.toLowerCase() : null;
  }

  get savedFg(): string | null {
    return this.colourMode === 'CUSTOM' ? this.effectiveFg.toLowerCase() : null;
  }

  setBg(hex: string): void {
    this.bg = hex;
    this.colourMode = 'CUSTOM';
  }

  // The hex boxes: only take a full #rrggbb.
  onHex(which: 'bg' | 'fg', value: string): void {
    const v = value.trim().startsWith('#') ? value.trim() : '#' + value.trim();
    if (!/^#[0-9a-f]{6}$/i.test(v)) return;
    if (which === 'bg') this.bg = v.toLowerCase();
    else {
      this.fg = v.toLowerCase();
      this.textMode = 'CUSTOM';
    }
  }

  get dirty(): boolean {
    return this.snapshot !== this.original;
  }

  // ---- Previous / next (list order) ----
  get ordered(): AdminLabel[] {
    return [...this.labels].sort(labelOrder);
  }

  get position(): number {
    return this.editing ? this.ordered.findIndex((l) => l.id === this.editing!.id) : -1;
  }

  get prev(): AdminLabel | null {
    return this.position > 0 ? this.ordered[this.position - 1] : null;
  }

  get next(): AdminLabel | null {
    const i = this.position;
    return i >= 0 && i < this.ordered.length - 1 ? this.ordered[i + 1] : null;
  }

  goTo(l: AdminLabel | null): void {
    if (!l || this.saving) return;
    confirmLeave(this.dialog, this.dirty, this.editing?.title ?? '', l.title).subscribe((ok) => {
      if (ok) this.router.navigate(['/labels/edit', l.id]);
    });
  }

  onTitle(value: string): void {
    this.title = value;
    if (!this.textTouched) this.text = value.toUpperCase();
  }

  onText(value: string): void {
    this.text = value;
    this.textTouched = true;
  }

  pick(s: string): void {
    this.text = s;
    this.textTouched = true;
    if (!this.title.trim()) this.title = s.charAt(0) + s.slice(1).toLowerCase();
  }

  get style() {
    return labelStyle(this.title.trim(), this.text.trim());
  }

  get formError(): string | null {
    if (!this.title.trim()) return 'Give the label a name';
    if (!this.text.trim()) return 'Enter the text to show on the product';
    if (this.title.trim().length > 100) return 'The name can be at most 100 characters';
    if (this.text.trim().length > 300) return 'The text can be at most 300 characters';
    return null;
  }

  // Another label with the same name - allowed, but worth knowing.
  get sameName(): AdminLabel | undefined {
    const t = this.title.trim().toLowerCase();
    return t ? this.labels.find((l) => l.id !== this.editing?.id && l.title.trim().toLowerCase() === t) : undefined;
  }

  save(): void {
    if (this.formError || this.saving) return;
    this.saving = true;
    const request = this.editing
      ? this.productService.updateLabel(this.editing.id, this.labelInput)
      : this.productService.createLabel(this.labelInput);
    request.subscribe({
      next: (saved) => {
        this.saving = false;
        this.alertService.alert(this.editing ? 'Label updated' : 'Label created', 'success');
        const id = this.editing?.id ?? saved?.id;
        this.router.navigate(['/labels'], id ? { queryParams: { selected: id } } : {});
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not save the label', 'error');
      },
    });
  }

  private get labelInput() {
    return { title: this.title.trim(), label: this.text.trim(), bgColor: this.savedBg, textColor: this.savedFg };
  }

  remove(): void {
    const l = this.editing;
    if (!l || !this.canDelete) return;
    const n = l.products;
    this.dialog
      .open(ConfirmDialogComponent, {
        data: {
          title: 'Delete label',
          message: `Delete "${l.title}"?`,
          detail: n ? `It's on ${n} product${n === 1 ? '' : 's'} - it will be taken off ${n === 1 ? 'it' : 'them'} too.` : "No products carry it. This can't be undone.",
          confirmText: 'Delete label',
          tone: 'danger',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (!result?.confirmation) return;
        this.productService.deleteLabel(l.id, n > 0).subscribe({
          next: () => {
            this.alertService.alert('Label deleted', 'success');
            this.router.navigate(['/labels']);
          },
          error: (error) => this.alertService.alert(error?.error?.message || 'Could not delete the label', 'error'),
        });
      });
  }
}
